import axios from 'axios';

const endpoints = {
    trainings: () => `/api/trainings`,
    training: (trainingId) => `/api/trainings/${trainingId}`,
    allTrainings: () => `/api/trainings/getAllTrainings`,
    recordEvaluationReport: () => `/api/trainings/recordEvaluationReport`,
    updateEvaluationReport: (evaluationId) => `/api/trainings/updateEvaluationReport/${evaluationId}`,
    updateEvaluationReportByBeacon: (evaluationId) => `/api/trainings/updateEvaluationReportByBeacon/${evaluationId}`,
};

class TrainingService {
    constructor() {
        this.updatingEvaluationReport = false;
        this.pendingReport = null;
    }

    async getTrainings(payload) {
        const response = await axios.get(getAPIUrl() + endpoints.trainings(), {
            params: payload
        });
        return response.data;
    }

    async getAllTrainings(payload) {
        const response = await axios.get(getAPIUrl() + endpoints.allTrainings(), {
            params: payload
        });
        return response.data;
    }

    async getTraining(trainingId) {
        const response = await axios.get(getAPIUrl() + endpoints.training(trainingId));
        return response.data;
    }

    async recordEvaluationReport(payload) {
        const response = await axios.post(getAPIUrl() + endpoints.recordEvaluationReport(), payload);
        return response.data.data;
    }

    async updateEvaluationReport(evaluationId, payload) {
        const serializedPayload = JSON.stringify(payload);
        if (this.updatingEvaluationReport) {
            this.pendingReport = serializedPayload;
            return;
        }

        this.updatingEvaluationReport = true;
        const response = await axios.post(getAPIUrl() + endpoints.updateEvaluationReport(evaluationId), payload);
        this.updatingEvaluationReport = false;

        if (this.pendingReport && this.pendingReport !== serializedPayload) {
            //don't await this call because we don't care about the result
            this.updateEvaluationReport(evaluationId, JSON.parse(this.pendingReport));
        }

        return response.data.data;
    }

    updateEvaluationReportByBeacon(evaluationId, payload) {
        if (!navigator || !navigator.sendBeacon) {
            return;
        }

        const url = getAPIUrl() + endpoints.updateEvaluationReportByBeacon(evaluationId);
        navigator.sendBeacon(url, JSON.stringify(payload));
    }

    _prepareChapters(chapters, resetChapterList = false, progressBar = true, isPreview = false) {
        let self = this;

        const training = TrainingTreeController._trainingFeature.getActiveTraining();
        if (wv.effectController.stoppedTrainingId === training.id) {
            return;
        }
        training.loadResources(progressBar, isPreview);

        if (chapters.length === 1) {

            let trainingChapter = chapters[0];

            if (resetChapterList) {
                const trainingListMenu = wv.promptWindowList.find(listMenu => listMenu.listName === ListPromptNameEnum.TrainingChapterList);
                if (trainingListMenu) {
                    trainingListMenu.destroy();
                }
            }

            self.confirmTrainingStart(true, {
                value: trainingChapter.id,
                name: trainingChapter.name
            }, chapters);

        } else {
            this.showTrainingChapterList(chapters);
        }
    }

    async initiateTrainingFromMeetingData() {
        const trainingId = wv.effectController.meetingInfo.data.trainingId;
        const trainingData = await this.getTraining(trainingId);
        const trainingChapters = trainingData.data.chapters;

        wv.effectController.convertPDFToImages = !!(trainingData.data.metadata && trainingData.data.metadata.convertPDFToImages);

        await TrainingTreeController._trainingFeature.setActiveTraining(trainingId);
        this._prepareChapters(trainingChapters);
    }

    showTrainingChapterList(chaptersData) {
        let self = this;
        (chaptersData && chaptersData.length > 1) ? PickerManager.addNewPicker(
            cameraTracker,
            "Select a training chapter to play",
            chaptersData.map(trainingChapter => {
                return {
                    text: trainingChapter.name,
                    value: {
                        text: trainingChapter.name,
                        value: trainingChapter.id
                    }
                };
            })
            , function (value) {
                self.confirmTrainingStart(true, value, chaptersData);
            }
        ) : null;
    }

    async confirmTrainingStart(value, trainingChapterName, chaptersData) {
        if (value) {
            await playChapterOnResourceLoad();
            this.showTrainingChapterList(chaptersData);
        } else {
            if (wv.effectController.trainingMeeting) {
                wv.closeThisMeeting();
            }
        }

        async function playChapterOnResourceLoad() {
            if (!wv.isTrainingResourceLoading) {
                await playChapter();
                return;
            }
            await waitForResource();
        }

        async function waitForResource() {
            if (wv.isTrainingResourceLoading) {
                setTimeout(waitForResource, 100);
            } else {
                await playChapter();
            }
        }

        async function playChapter() {
            wv.allVolumesForFaces.visible = true;
            await TrainingTreeController._trainingFeature.playChapter(trainingChapterName.value);
        }
    }

    async fetchTrainingData() {
        let self = this;
        try {
            if (self.isTemplateMeeting()) {
                return self.getTemplateTrainingData();
            }

            let {data} = await this.getAllTrainings({
                projectID: wv.effectController.meetingInfo.data.project._id
            });
            return await fetchTemplateTrainingData(data);
        } catch (error) {
            return await fetchTemplateTrainingData();
        }

        async function fetchTemplateTrainingData(allTrainingData) {
            if (self.isTemplateMeeting()) {
                return await self.getTemplateTrainingData(allTrainingData);
            }

            return allTrainingData;
        }
    }

    isTemplateMeeting() {
        return wv.effectController.meetingInfo.data.type
            === MeetingType.TRAINING;
    }

    filterTrainingByMeetingType(data) {
        const trainingId = wv.effectController.meetingInfo.data.trainingId;
        return data.filter(training => training._id === trainingId);
    }

    async getTemplateTrainingData(allTrainingData) {
        if (allTrainingData && allTrainingData.length > 0) {
            allTrainingData = this.filterTrainingByMeetingType(allTrainingData);
        } else {
            allTrainingData = [];
            const trainingData = await this.getTraining(wv.effectController.meetingInfo.data.trainingId);
            if (!trainingData) {
                return allTrainingData;
            }

            allTrainingData.push(trainingData.data);
        }

        return allTrainingData;
    }

    async loadTraining(trainingData, isPreview = false) {
        await updateMenuForTraining();
        wv.effectController.stoppedTrainingId = '';
        wv.effectController.loadedTrainingId = trainingData._id;

        const getUpdatedTraining = (trainingData) => {
            trainingData.id = trainingData._id;
            return trainingData;
        };

        wv.effectController.serverlessTrainingRunning = true;
        wv.effectController.convertPDFToImages = false;
        trainingData = getUpdatedTraining(trainingData);
        const trainingChapters = trainingData.chapters;
        BoundingBoxFeatureManager.resetAllBoundingBoxVisibility();
        await TrainingTreeController._trainingFeature.resetScene();
        TrainingTreeController._trainingFeature.setCustomActiveTraining(trainingData);

        await updateWorkspaceModifiability(true);
        this._prepareChapters(trainingChapters, true, true, isPreview);
    }

    stopTraining(trainingId) {
        wv.effectController.loadedTrainingId = '';
        model_load_helper.model_appended = false;
        modelLoadingProgressBar.show(false);
        BoundingBoxFeatureManager.resetAllBoundingBoxVisibility();
        wv.effectController.stoppedTrainingId = trainingId;
        TrainingTreeController._trainingFeature.stopPlayer(true);
        InstructionManager.RemoveAllInstructionMenus();
        OverallMenuController.forceRemoveAllMenus();
    }

    async setTraining(trainingData, cores) {
        ViewPointManager.initializedViewpoints = false;
        await updateMenuForTraining(false);
        ARAuthoringController.loadARAuthoringConfigFromTrainingData(trainingData);
        wv.effectController.convertPDFToImages = false;
        TrainingTreeController._trainingFeature.setCustomActiveTraining(trainingData);
        SceneMenuController.updateLoadTrainingStateMenu();
        StepMenuController.updateLoadTrainingStepMenu();
        await SceneMenuController.updateAvailableModelListMenu(cores);
        await SceneMenuController.updateInformationTagListMenu(cores);
        SceneMenuController.update360ImageListMenu(cores);
        SceneMenuController.update360VideoListMenu(cores);
        SceneMenuController.updateMediaPosterListMenu(cores);
        SceneMenuController.updateMediaVideoListMenu(cores);
        SceneMenuController.updateMediaPDFListMenu(cores);
        HelperObjectManager.reserveObjectNames(trainingData);
    }

    isAnyTrainingRunning() {
        return wv.effectController.trainingMeeting || wv.effectController.serverlessTrainingRunning;
    }
}

export default new TrainingService();
